import React from "react";
import { Box, Typography, Grid, Button } from "@mui/material";
import AppleIcon from "@mui/icons-material/Apple";
import { useParams, Link } from "react-router-dom";

function Pinta() {
  const { lang } = useParams();

  // Objeto con todos los idiomas: español, inglés, francés, alemán, italiano
  const texts = {
    en: {
      mainTitle: "Pinta",
      subTitle: "The Ultimate Coloring App for Adults",
      featuresTitle: "Key Features",
      comingSoon: "Available now on the App Store for iPad and iPhone!",
      features: [
        "Unlimited Creativity: Experience a unique coloring journey with templates designed exclusively for adults.",
        "Complete Customization: Adjust colors, strokes, and details to match your style.",
        "User-Friendly Interface: Designed for anyone to enjoy the art of coloring, regardless of skill level.",
        "Relaxation Guaranteed: Find moments of calm and focus while coloring.",
        "iCloud Integration: Save and access your creations from any compatible device.",
      ],
      storeButton: "View on the App Store",
      terms: "Terms & Conditions",
      policy: "Privacy Policy",
    },
    es: {
      mainTitle: "Pinta",
      subTitle: "La App definitiva de Coloreado para Adultos",
      featuresTitle: "Características Principales",
      comingSoon: "¡Disponible ahora en la App Store para iPad y iPhone!",
      features: [
        "Creatividad sin límites: Descubre una experiencia única de coloreado con plantillas diseñadas exclusivamente para adultos.",
        "Personalización total: Ajusta colores, trazos y detalles según tu estilo.",
        "Interfaz amigable: Diseñada para que cualquiera pueda disfrutar del arte de colorear, sin importar su nivel de experiencia.",
        "Relajación asegurada: Encuentra momentos de calma y enfoque mientras coloreas.",
        "Integración con iCloud: Guarda y accede a tus obras desde cualquier dispositivo compatible.",
      ],
      storeButton: "Ver en la App Store",
      terms: "Términos y Condiciones",
      policy: "Política de Privacidad",
    },
    fr: {
      mainTitle: "Pinta",
      subTitle: "L'application de coloriage ultime pour adultes",
      featuresTitle: "Caractéristiques Clés",
      comingSoon: "Disponible maintenant sur l'App Store pour iPad et iPhone !",
      features: [
        "Créativité illimitée : découvrez une expérience de coloriage unique avec des modèles conçus exclusivement pour les adultes.",
        "Personnalisation totale : ajustez les couleurs, les traits et les détails à votre style.",
        "Interface conviviale : conçue pour que chacun puisse profiter du coloriage, quel que soit son niveau.",
        "Détente garantie : trouvez des moments de calme et de concentration en coloriant.",
        "Intégration iCloud : enregistrez et accédez à vos créations depuis tout appareil compatible.",
      ],
      storeButton: "Voir sur l’App Store",
      terms: "Termes et Conditions",
      policy: "Politique de Confidentialité",
    },
    de: {
      mainTitle: "Pinta",
      subTitle: "Die ultimative Mal-App für Erwachsene",
      featuresTitle: "Hauptmerkmale",
      comingSoon: "Jetzt im App Store für iPad und iPhone verfügbar!",
      features: [
        "Unbegrenzte Kreativität: Erleben Sie ein einzigartiges Farberlebnis mit Vorlagen, die ausschließlich für Erwachsene entworfen wurden.",
        "Vollständige Anpassung: Passen Sie Farben, Striche und Details an Ihren Stil an.",
        "Benutzerfreundliches Interface: Entwickelt, damit jeder Freude am Ausmalen hat – unabhängig vom Erfahrungslevel.",
        "Garantierte Entspannung: Finden Sie Momente der Ruhe und Konzentration beim Ausmalen.",
        "iCloud-Integration: Speichern und greifen Sie auf Ihre Kreationen von jedem kompatiblen Gerät aus zu.",
      ],
      storeButton: "Im App Store ansehen",
      terms: "Nutzungsbedingungen",
      policy: "Datenschutzrichtlinie",
    },
    it: {
      mainTitle: "Pinta",
      subTitle: "L'app definitiva di colorazione per adulti",
      featuresTitle: "Caratteristiche Principali",
      comingSoon: "Ora disponibile su App Store per iPad e iPhone!",
      features: [
        "Creatività illimitata: scopri un’esperienza di colorazione unica con modelli progettati esclusivamente per adulti.",
        "Personalizzazione completa: adatta colori, tratti e dettagli al tuo stile.",
        "Interfaccia intuitiva: progettata per permettere a chiunque di godersi l’arte del colorare, indipendentemente dal livello di abilità.",
        "Rilassamento garantito: trova momenti di calma e concentrazione mentre colori.",
        "Integrazione con iCloud: salva e accedi alle tue creazioni da qualsiasi dispositivo compatibile.",
      ],
      storeButton: "Vedi sull'App Store",
      terms: "Termini e Condizioni",
      policy: "Informativa sulla Privacy",
    },
  };

  // Si lang no coincide con un idioma disponible, usa 'en' por defecto
  const t = texts[lang] || texts.en;

  return (
    <Box sx={{ maxWidth: 1100, margin: "0 auto", padding: 2, mt: 2 }}>
      <Typography variant="h3" gutterBottom align="center">
        {t.mainTitle}
      </Typography>

      {/* Si hay un subTitle, lo mostramos */}
      {t.subTitle && (
        <Typography variant="h5" gutterBottom align="center">
          {t.subTitle}
        </Typography>
      )}

      {/* Enlaces a Terms y Policy */}
      <Box sx={{ textAlign: "center", mt: 2, mb: 4 }}>
        <Link
          to={`/${lang}/pinta/terms`}
          style={{ textDecoration: "none", marginRight: 16 }}
        >
          <Button variant="outlined">{t.terms}</Button>
        </Link>
        <Link to={`/${lang}/pinta/privacy`} style={{ textDecoration: "none" }}>
          <Button variant="outlined">{t.policy}</Button>
        </Link>
      </Box>

      <Grid container spacing={4} sx={{ mt: 0 }}>
        {/* Lista de características */}
        <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
          <Typography variant="h4" gutterBottom>
            {t.featuresTitle}
          </Typography>
          <br />

          <ul>
            {t.features.map((feature, index) => (
              <Typography
                key={index}
                component="li"
                variant="body1"
                sx={{ mb: 2 }}
              >
                {feature}
              </Typography>
            ))}
          </ul>
        </Grid>

        {/* Imagen */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src="/resources/pinta-ipad.png"
            alt="PintaPro Logo"
            style={{ width: "100%", maxWidth: "500px" }}
          />
        </Grid>
      </Grid>

      {/* Disponible pronto */}
      <Box
        sx={{
          mt: 6,
          textAlign: "center",
          backgroundColor: "#f5f5f5",
          padding: 4,
          borderRadius: 2,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t.comingSoon}
        </Typography>
        <a
          href={`https://apps.apple.com/${lang}/app/id6761273181`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <Button
            variant="contained"
            startIcon={<AppleIcon />}
            sx={{ mt: 2, backgroundColor: "#0071e3", color: "#fff" }}
          >
            {t.storeButton}
          </Button>
        </a>
      </Box>
    </Box>
  );
}

export default Pinta;
